import { useState } from 'react';
import { BasketItem } from '../page';

interface ExchangeModalProps {
  onClose: () => void;
  onExchangeComplete: (returnItems: BasketItem[], newItems: BasketItem[]) => void;
}

interface ExchangeItem extends BasketItem {
  returnReason?: string;
}

export default function ExchangeModal({ onClose, onExchangeComplete }: ExchangeModalProps) {
  const [step, setStep] = useState<'return' | 'new' | 'summary'>('return');
  const [returnItems, setReturnItems] = useState<ExchangeItem[]>([]);
  const [newItems, setNewItems] = useState<BasketItem[]>([]);
  const [searchCode, setSearchCode] = useState('');
  const [returnSearchCode, setReturnSearchCode] = useState('');
  const [selectedReturnItem, setSelectedReturnItem] = useState<ExchangeItem | null>(null);
  const [returnReason, setReturnReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Mock product database
  const mockProducts = [
    { code: 'MLK-001', name: 'Organic Whole Milk 1L', price: 4.99, tax: true },
    { code: 'BRD-001', name: 'Fresh Bread Whole Wheat', price: 3.49, tax: true },
    { code: 'COF-001', name: 'Premium Coffee Beans 500g', price: 12.99, tax: true },
    { code: 'YOG-001', name: 'Greek Yogurt 500g', price: 5.49, tax: true },
    { code: 'BAN-001', name: 'Organic Bananas (per kg)', price: 2.99, tax: false },
    { code: 'EGG-001', name: 'Free Range Eggs (12 pack)', price: 6.99, tax: true },
    { code: 'CHE-001', name: 'Cheddar Cheese 200g', price: 4.79, tax: true },
    { code: 'JUI-001', name: 'Orange Juice 1L', price: 4.99, tax: true },
    { code: 'PAS-001', name: 'Pasta Penne 500g', price: 2.49, tax: true },
    { code: 'SAU-001', name: 'Tomato Sauce 400g', price: 1.99, tax: true },
  ];

  const returnReasons = [
    'Defective/Damaged',
    'Wrong Size',
    'Wrong Color',
    'Changed Mind',
    'Better Price Elsewhere',
    'Quality Issue',
    'Not as Described',
    'Other',
  ];

  const handleAddReturnItem = () => {
    if (!returnSearchCode.trim()) return;

    const product = mockProducts.find(p => 
      p.code.toLowerCase() === returnSearchCode.toLowerCase()
    );

    if (!product) {
      alert('Product not found. Please check the code.');
      return;
    }

    const newLine = returnItems.length + 1;
    const newItem: ExchangeItem = {
      line: newLine,
      code: product.code,
      name: product.name,
      qty: 1,
      price: product.price,
      discount: false,
      tax: product.tax,
      total: product.price,
      returnReason: '',
    };

    setReturnItems([...returnItems, newItem]);
    setReturnSearchCode('');
  };

  const handleAddNewItem = () => {
    if (!searchCode.trim()) return;

    const product = mockProducts.find(p => 
      p.code.toLowerCase() === searchCode.toLowerCase()
    );

    if (!product) {
      alert('Product not found. Please check the code.');
      return;
    }

    const newLine = newItems.length + 1;
    const newItem: BasketItem = {
      line: newLine,
      code: product.code,
      name: product.name,
      qty: 1,
      price: product.price,
      discount: false,
      tax: product.tax,
      total: product.price,
    };

    setNewItems([...newItems, newItem]);
    setSearchCode('');
  };

  const handleUpdateReturnQty = (index: number, qty: number) => {
    if (qty <= 0) return;
    const updated = [...returnItems];
    updated[index].qty = qty;
    updated[index].total = qty * updated[index].price;
    setReturnItems(updated);
  };

  const handleUpdateNewQty = (index: number, qty: number) => {
    if (qty <= 0) return;
    const updated = [...newItems];
    updated[index].qty = qty;
    updated[index].total = qty * updated[index].price;
    setNewItems(updated);
  };

  const handleRemoveReturnItem = (index: number) => {
    setReturnItems(returnItems.filter((_, i) => i !== index));
  };

  const handleRemoveNewItem = (index: number) => {
    setNewItems(newItems.filter((_, i) => i !== index));
  };

  const handleSetReturnReason = (item: ExchangeItem) => {
    setSelectedReturnItem(item);
    setReturnReason(item.returnReason || '');
  };

  const handleSaveReturnReason = () => {
    if (!selectedReturnItem || !returnReason) return;

    const updated = returnItems.map(item => 
      item.line === selectedReturnItem.line 
        ? { ...item, returnReason }
        : item
    );
    setReturnItems(updated);
    setSelectedReturnItem(null);
    setReturnReason('');
  };

  const canProceedToNew = returnItems.length > 0 && returnItems.every(item => item.returnReason);
  const canProceedToSummary = newItems.length > 0;

  const returnTotal = returnItems.reduce((sum, item) => sum + item.total, 0);
  const newTotal = newItems.reduce((sum, item) => sum + item.total, 0);
  const difference = newTotal - returnTotal;

  const handleCompleteExchange = () => {
    setIsProcessing(true);
    
    setTimeout(() => {
      onExchangeComplete(returnItems, newItems);
      onClose();
    }, 1500);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-purple-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold whitespace-nowrap">Product Exchange</h2>
            <p className="text-sm text-purple-100 whitespace-nowrap">
              {step === 'return' && 'Step 1: Select items to return'}
              {step === 'new' && 'Step 2: Select new items'}
              {step === 'summary' && 'Step 3: Review and complete'}
            </p>
          </div>
          <button 
            onClick={onClose}
            disabled={isProcessing}
            className="w-8 h-8 flex items-center justify-center hover:bg-purple-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Progress Steps */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-center gap-4">
            <div className={`flex items-center gap-2 ${step === 'return' ? 'text-purple-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 flex items-center justify-center rounded-full font-bold ${
                step === 'return' ? 'bg-purple-600 text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                1
              </div>
              <span className="text-sm font-semibold whitespace-nowrap">Return Items</span>
            </div>
            <i className="ri-arrow-right-line text-gray-400"></i>
            <div className={`flex items-center gap-2 ${step === 'new' ? 'text-purple-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 flex items-center justify-center rounded-full font-bold ${
                step === 'new' ? 'bg-purple-600 text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                2
              </div>
              <span className="text-sm font-semibold whitespace-nowrap">New Items</span>
            </div>
            <i className="ri-arrow-right-line text-gray-400"></i>
            <div className={`flex items-center gap-2 ${step === 'summary' ? 'text-purple-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 flex items-center justify-center rounded-full font-bold ${
                step === 'summary' ? 'bg-purple-600 text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                3
              </div>
              <span className="text-sm font-semibold whitespace-nowrap">Summary</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Return Items */}
          {step === 'return' && (
            <div>
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                <div>
                  <p className="text-sm font-semibold text-blue-900 mb-1 whitespace-nowrap">Return Items</p>
                  <p className="text-xs text-blue-800 whitespace-nowrap">Scan or enter the product codes for items being returned. A return reason is required for each item.</p>
                </div>
              </div>

              {/* Add Return Item */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">Scan or Enter Product Code</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={returnSearchCode}
                    onChange={(e) => setReturnSearchCode(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddReturnItem()}
                    placeholder="Enter product code (e.g., MLK-001)"
                    className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                    autoFocus
                  />
                  <button
                    onClick={handleAddReturnItem}
                    className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line mr-1"></i>
                    Add
                  </button>
                </div>
              </div>

              {/* Return Items List */}
              {returnItems.length === 0 ? (
                <div className="p-12 text-center text-gray-400">
                  <i className="ri-shopping-basket-line text-6xl mb-3"></i>
                  <p className="text-sm whitespace-nowrap">No return items added yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {returnItems.map((item, index) => (
                    <div key={item.line} className="bg-white border-2 border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 mb-1">{item.name}</p>
                          <p className="text-sm text-gray-600 whitespace-nowrap">Code: {item.code}</p>
                        </div>
                        <button
                          onClick={() => handleRemoveReturnItem(index)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-red-50 rounded cursor-pointer"
                          title="Remove"
                        >
                          <i className="ri-delete-bin-line text-red-600"></i>
                        </button>
                      </div>

                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1 whitespace-nowrap">Quantity</label>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleUpdateReturnQty(index, item.qty - 1)}
                              className="w-7 h-7 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded cursor-pointer"
                            >
                              <i className="ri-subtract-line"></i>
                            </button>
                            <input
                              type="number"
                              value={item.qty}
                              onChange={(e) => handleUpdateReturnQty(index, parseFloat(e.target.value) || 1)}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                              min="1"
                              step="0.1"
                            />
                            <button
                              onClick={() => handleUpdateReturnQty(index, item.qty + 1)}
                              className="w-7 h-7 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded cursor-pointer"
                            >
                              <i className="ri-add-line"></i>
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1 whitespace-nowrap">Unit Price</label>
                          <p className="text-sm font-semibold text-gray-900 py-1 whitespace-nowrap">${item.price.toFixed(2)}</p>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1 whitespace-nowrap">Total</label>
                          <p className="text-sm font-bold text-gray-900 py-1 whitespace-nowrap">${item.total.toFixed(2)}</p>
                        </div>
                      </div>

                      {/* Return Reason */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1 whitespace-nowrap">Return Reason *</label>
                        {item.returnReason ? (
                          <div className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded">
                            <span className="text-sm text-green-800 whitespace-nowrap">{item.returnReason}</span>
                            <button
                              onClick={() => handleSetReturnReason(item)}
                              className="text-xs text-green-700 hover:text-green-900 font-semibold whitespace-nowrap cursor-pointer"
                            >
                              Change
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleSetReturnReason(item)}
                            className="w-full px-3 py-2 border-2 border-dashed border-gray-300 hover:border-purple-500 text-gray-600 hover:text-purple-600 rounded text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
                          >
                            <i className="ri-add-line mr-1"></i>
                            Set Return Reason
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Return Reason Modal */}
              {selectedReturnItem && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 whitespace-nowrap">Select Return Reason</h3>
                    <p className="text-sm text-gray-600 mb-4 whitespace-nowrap">Product: {selectedReturnItem.name}</p>
                    
                    <div className="space-y-2 mb-6">
                      {returnReasons.map((reason) => (
                        <button
                          key={reason}
                          onClick={() => setReturnReason(reason)}
                          className={`w-full p-3 rounded-lg border-2 text-left transition-all cursor-pointer ${
                            returnReason === reason
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                          }`}
                        >
                          <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{reason}</span>
                        </button>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedReturnItem(null);
                          setReturnReason('');
                        }}
                        className="flex-1 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveReturnReason}
                        disabled={!returnReason}
                        className={`flex-1 px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition-colors ${
                          returnReason
                            ? 'bg-purple-600 hover:bg-purple-700 text-white cursor-pointer'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: New Items */}
          {step === 'new' && (
            <div>
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                <div>
                  <p className="text-sm font-semibold text-blue-900 mb-1 whitespace-nowrap">New Items</p>
                  <p className="text-xs text-blue-800 whitespace-nowrap">Scan or enter the product codes for new items the customer wants to purchase.</p>
                </div>
              </div>

              {/* Add New Item */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">Scan or Enter Product Code</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={searchCode}
                    onChange={(e) => setSearchCode(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddNewItem()}
                    placeholder="Enter product code (e.g., MLK-001)"
                    className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                    autoFocus
                  />
                  <button
                    onClick={handleAddNewItem}
                    className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line mr-1"></i>
                    Add
                  </button>
                </div>
              </div>

              {/* New Items List */}
              {newItems.length === 0 ? (
                <div className="p-12 text-center text-gray-400">
                  <i className="ri-shopping-cart-line text-6xl mb-3"></i>
                  <p className="text-sm whitespace-nowrap">No new items added yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {newItems.map((item, index) => (
                    <div key={item.line} className="bg-white border-2 border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 mb-1">{item.name}</p>
                          <p className="text-sm text-gray-600 whitespace-nowrap">Code: {item.code}</p>
                        </div>
                        <button
                          onClick={() => handleRemoveNewItem(index)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-red-50 rounded cursor-pointer"
                          title="Remove"
                        >
                          <i className="ri-delete-bin-line text-red-600"></i>
                        </button>
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1 whitespace-nowrap">Quantity</label>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleUpdateNewQty(index, item.qty - 1)}
                              className="w-7 h-7 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded cursor-pointer"
                            >
                              <i className="ri-subtract-line"></i>
                            </button>
                            <input
                              type="number"
                              value={item.qty}
                              onChange={(e) => handleUpdateNewQty(index, parseFloat(e.target.value) || 1)}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                              min="1"
                              step="0.1"
                            />
                            <button
                              onClick={() => handleUpdateNewQty(index, item.qty + 1)}
                              className="w-7 h-7 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded cursor-pointer"
                            >
                              <i className="ri-add-line"></i>
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1 whitespace-nowrap">Unit Price</label>
                          <p className="text-sm font-semibold text-gray-900 py-1 whitespace-nowrap">${item.price.toFixed(2)}</p>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1 whitespace-nowrap">Total</label>
                          <p className="text-sm font-bold text-gray-900 py-1 whitespace-nowrap">${item.total.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Summary */}
          {step === 'summary' && (
            <div>
              <div className="grid grid-cols-2 gap-6 mb-6">
                {/* Return Summary */}
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                  <h3 className="text-sm font-bold text-red-900 mb-3 flex items-center gap-2">
                    <i className="ri-arrow-left-line"></i>
                    Return Items ({returnItems.length})
                  </h3>
                  <div className="space-y-2 mb-3">
                    {returnItems.map((item) => (
                      <div key={item.line} className="bg-white rounded p-2">
                        <p className="text-sm font-semibold text-gray-900 mb-1">{item.name}</p>
                        <div className="flex items-center justify-between text-xs text-gray-600">
                          <span className="whitespace-nowrap">Qty: {item.qty} × ${item.price.toFixed(2)}</span>
                          <span className="font-bold whitespace-nowrap">${item.total.toFixed(2)}</span>
                        </div>
                        <p className="text-xs text-red-700 mt-1 whitespace-nowrap">Reason: {item.returnReason}</p>
                      </div>
                    ))}
                  </div>
                  <div className="border-t-2 border-red-300 pt-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-red-900 whitespace-nowrap">Return Total:</span>
                      <span className="text-lg font-bold text-red-900 whitespace-nowrap">${returnTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* New Items Summary */}
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                  <h3 className="text-sm font-bold text-green-900 mb-3 flex items-center gap-2">
                    <i className="ri-arrow-right-line"></i>
                    New Items ({newItems.length})
                  </h3>
                  <div className="space-y-2 mb-3">
                    {newItems.map((item) => (
                      <div key={item.line} className="bg-white rounded p-2">
                        <p className="text-sm font-semibold text-gray-900 mb-1">{item.name}</p>
                        <div className="flex items-center justify-between text-xs text-gray-600">
                          <span className="whitespace-nowrap">Qty: {item.qty} × ${item.price.toFixed(2)}</span>
                          <span className="font-bold whitespace-nowrap">${item.total.toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t-2 border-green-300 pt-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-green-900 whitespace-nowrap">New Total:</span>
                      <span className="text-lg font-bold text-green-900 whitespace-nowrap">${newTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Difference Calculation */}
              <div className={`p-6 rounded-lg border-2 ${
                difference > 0 
                  ? 'bg-orange-50 border-orange-200' 
                  : difference < 0 
                  ? 'bg-blue-50 border-blue-200' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-bold text-gray-900 whitespace-nowrap">Exchange Difference:</span>
                  <span className={`text-3xl font-bold ${
                    difference > 0 
                      ? 'text-orange-600' 
                      : difference < 0 
                      ? 'text-blue-600' 
                      : 'text-gray-900'
                  }`}>
                    {difference > 0 ? '+' : ''}{difference < 0 ? '-' : ''}${Math.abs(difference).toFixed(2)}
                  </span>
                </div>
                <p className="text-sm text-gray-700 whitespace-nowrap">
                  {difference > 0 && 'Customer needs to pay the difference'}
                  {difference < 0 && 'Customer will receive a refund'}
                  {difference === 0 && 'Even exchange - no payment required'}
                </p>
              </div>

              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
                <i className="ri-alert-line text-yellow-600 text-xl mt-0.5"></i>
                <div>
                  <p className="text-sm font-semibold text-yellow-900 mb-1 whitespace-nowrap">Important</p>
                  <p className="text-xs text-yellow-800 whitespace-nowrap">
                    {difference > 0 && 'Proceed to payment to collect the difference amount.'}
                    {difference < 0 && 'Refund will be processed to the original payment method.'}
                    {difference === 0 && 'Complete the exchange transaction.'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>

          <div className="flex gap-2">
            {step !== 'return' && (
              <button
                onClick={() => setStep(step === 'summary' ? 'new' : 'return')}
                disabled={isProcessing}
                className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-arrow-left-line mr-1"></i>
                Back
              </button>
            )}

            {step === 'return' && (
              <button
                onClick={() => setStep('new')}
                disabled={!canProceedToNew}
                className={`px-8 py-3 rounded-lg font-bold whitespace-nowrap transition-colors ${
                  canProceedToNew
                    ? 'bg-purple-600 hover:bg-purple-700 text-white cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Next: Add New Items
                <i className="ri-arrow-right-line ml-1"></i>
              </button>
            )}

            {step === 'new' && (
              <button
                onClick={() => setStep('summary')}
                disabled={!canProceedToSummary}
                className={`px-8 py-3 rounded-lg font-bold whitespace-nowrap transition-colors ${
                  canProceedToSummary
                    ? 'bg-purple-600 hover:bg-purple-700 text-white cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Next: Review Summary
                <i className="ri-arrow-right-line ml-1"></i>
              </button>
            )}

            {step === 'summary' && (
              <button
                onClick={handleCompleteExchange}
                disabled={isProcessing}
                className={`px-8 py-3 rounded-lg font-bold whitespace-nowrap transition-colors ${
                  isProcessing
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700 text-white cursor-pointer'
                }`}
              >
                {isProcessing ? (
                  <>
                    <i className="ri-loader-4-line mr-2 animate-spin"></i>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="ri-check-line mr-2"></i>
                    Complete Exchange
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
